import { Button } from '@heidi/design-system/button';
import { CopyButton } from '@heidi/design-system/copy-button';
import type { CopyResult } from '@heidi/design-system/copy-button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import { Tooltip, TooltipContent, TooltipTrigger } from '@heidi/design-system/tooltip';
import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import {
  // Bookmark, // TODO: Restore when Save Snippet is ready
  Ellipsis,
  // FilePen, // TODO: Restore when Draft is ready
  FilePlus,
  Hash,
  ListPlus,
  Replace,
  Share,
} from 'lucide-react';
import { forwardRef } from 'react';
import {
  ActionDropdown,
  type ActionDropdownItem,
  type ActionDropdownNudge,
} from './ActionDropdown';
import { useOverlayTriggerTooltip } from '@heidi/utils';

interface ActionButtonProps {
  /** Sized by the underlying `Button`, so pass a bare glyph. */
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
  /**
   * Applied to the `Button`, not the glyph — colour set on the SVG shadows the
   * ghost variant's `hover:text-text-primary` and the icon never lightens.
   */
  className?: string;
}

/**
 * Reusable icon button with tooltip.
 *
 * Forwards a ref to the underlying button so callers can anchor floating UI
 * (popovers, tooltips) to a specific row entry — used by the Evidence copy
 * confirmation popover (EVI-1452 / SWR-88).
 */
export const ActionButton = forwardRef<HTMLButtonElement, ActionButtonProps>(
  function ActionButton({ icon, label, onClick, disabled, testId, className }, ref) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            ref={ref}
            variant="ghost"
            size="md"
            iconOnly
            onClick={onClick}
            className={className}
            aria-label={label}
            disabled={disabled}
            data-testid={testId}
          >
            {icon}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    );
  }
);

interface ActionRowButtonsProps {
  /**
   * Performs the clipboard write. Return `false` (deferred behind the copy
   * confirmation popover, or a failed write) to suppress the copied feedback.
   */
  onCopy: () => CopyResult | Promise<CopyResult>;
  onShare?: () => void;
  onAddToNote?: () => void;
  onReplaceNote?: () => void;
  onCreateNewTab?: () => void;
  /**
   * EVI-1495 / SWR-45: Copy the chat ID to the clipboard for issue investigations.
   * Only rendered when defined (e.g. once the chat has been persisted and has an id).
   */
  onCopyChatId?: () => void;
  // onDraft: () => void; // TODO: Restore when Draft is ready
  // onSaveSnippet: () => void; // TODO: Restore when Save Snippet is ready
  hasSessionActions: boolean;
  /** Whether user is logged out (shows disabled items + nudge) */
  isLoggedOut?: boolean;
  /** Callback when user clicks sign up from nudge */
  onSignUp?: () => void;
  /**
   * Ref forwarded to the Copy button so callers can anchor floating UI to it
   * (used by the Evidence copy PHI/PII confirmation popover — EVI-1452 / SWR-88).
   */
  copyButtonRef?: React.RefObject<HTMLButtonElement>;
}

/**
 * Right-side action buttons for the reply actions row
 * Includes: Copy, Insert into, Draft, Save snippet, More actions dropdown
 */
export const ActionRowButtons = ({
  onCopy,
  onShare,
  onAddToNote,
  onReplaceNote,
  onCreateNewTab,
  onCopyChatId,
  // onDraft, // TODO: Restore when Draft is ready
  // onSaveSnippet, // TODO: Restore when Save Snippet is ready
  hasSessionActions,
  isLoggedOut = false,
  onSignUp,
  copyButtonRef,
}: ActionRowButtonsProps) => {
  const { t } = useTranslations();

  // Insert into dropdown items
  const insertIntoItems: ActionDropdownItem[] = [
    {
      id: 'add-to-note',
      label: t('chat.addToNote'),
      icon: ListPlus,
      onClick: onAddToNote ?? (() => {}),
      disabled: !onAddToNote,
    },
    {
      id: 'replace-note',
      label: t('chat.replaceNote'),
      icon: Replace,
      onClick: onReplaceNote ?? (() => {}),
      disabled: !onReplaceNote,
    },
    {
      id: 'create-new-tab',
      label: t('chat.createNewTab'),
      icon: FilePlus,
      onClick: onCreateNewTab ?? (() => {}),
      disabled: !onCreateNewTab,
    },
  ];

  // Nudge for logged out users
  const insertIntoNudge: ActionDropdownNudge | undefined =
    isLoggedOut && onSignUp
      ? {
          title: t('chat.nudge.title'),
          description: t('chat.nudge.description'),
          ctaLabel: t('chat.nudge.cta'),
          onCtaClick: onSignUp,
        }
      : undefined;

  return (
    <div className="flex flex-row items-center gap-0.5">
      {/* Share Button */}
      {onShare && (
        <ActionButton
          className="text-text-secondary"
          icon={<Share />}
          label={t('chat.share')}
          onClick={onShare}
        />
      )}

      {/* Copy Button */}
      <CopyButton
        ref={copyButtonRef}
        onCopy={onCopy}
        variant="ghost"
        size="md"
        className="text-text-secondary"
        label={t('tooltips.copy')}
        copiedLabel={t('shared.Copied')}
        data-testid="reply-action-copy"
      />

      {/* Insert into Dropdown - only when session exists OR logged out (show disabled) */}
      {(hasSessionActions || isLoggedOut) && (
        <ActionDropdown
          triggerIcon={<ListPlus />}
          triggerLabel={t('chat.insertInto')}
          items={insertIntoItems}
          isLoggedOut={isLoggedOut}
          nudge={insertIntoNudge}
          side="bottom"
          align="end"
          data-testid="action-insert-into"
        />
      )}

      {/*
       * More Actions Dropdown — overflow menu for chat-scoped actions.
       * EVI-1495 / SWR-45: houses "Copy chat ID" so it's discoverable
       * without crowding the primary action row.
       */}
      {onCopyChatId && <MoreActionsDropdown onCopyChatId={onCopyChatId} />}

      {/*
       * Draft Button - Hidden until functionality is implemented
       * TODO: Bring back when draft/document generation is ready
       * <ActionButton
       *   className="text-text-secondary"
       *   icon={<FilePen />}
       *   label={t('chat.draft')}
       *   onClick={onDraft}
       * />
       */}

      {/*
       * Save Snippet Button - Hidden until functionality is implemented
       * TODO: Bring back when save snippet is ready
       * <ActionButton
       *   className="text-text-secondary"
       *   icon={<Bookmark />}
       *   label={t('chat.saveSnippet')}
       *   onClick={onSaveSnippet}
       * />
       */}
    </div>
  );
};

interface MoreActionsDropdownProps {
  /**
   * EVI-1495 / SWR-45: Copy the chat ID to the clipboard for issue investigations.
   * Required because today this is the only item in the menu — when the dropdown
   * grows (e.g. Export, Print) make each item independently optional.
   */
  onCopyChatId: () => void;
}

/**
 * Overflow dropdown for chat-scoped actions that don't warrant their own icon
 * in the primary action row. Triggered by an ellipsis button.
 */
const MoreActionsDropdown = ({ onCopyChatId }: MoreActionsDropdownProps) => {
  const { t } = useTranslations();
  const { tooltipProps, contentProps } = useOverlayTriggerTooltip();

  return (
    <DropdownMenu>
      <Tooltip {...tooltipProps}>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="md"
              iconOnly
              className="text-text-secondary"
              aria-label={t('chat.moreActions')}
              data-testid="reply-action-more"
            >
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">{t('chat.moreActions')}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        {...contentProps}
        align="end"
        side="bottom"
        sideOffset={4}
        className={cn(
          'origin-top-right',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'duration-200 ease-out'
        )}
      >
        <DropdownMenuItem onClick={onCopyChatId} data-testid="reply-action-copy-chat-id">
          <Hash />
          {t('chat.copyChatId')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
